import React, { useState, useCallback, useEffect } from 'react';
import {
    InstantSearch,
    Configure,
    Pagination,
    useSearchBox,
    useStats,
} from 'react-instantsearch';
import CustomHits from './CustomHits';
import CustomRefinementList from './CustomRefinementList';
import SearchByNum from './SearchByNum';
import CustomDateFilter from './CustomDateFilter';
import {CustomClearRefinements} from './CustomClearRefinements';
import './Search.css';
import { algoliaSearchClient, ALGOLIA_APP_ID, ALGOLIA_API_KEY, ALGOLIA_INDEX_FICHES } from '../../api/algolia';

// Attributs recherchables par défaut (pour le sélecteur "Rechercher dans") si les
// settings Algolia ne sont pas encore chargés. À garder aligné avec l'index.
const SEARCHABLE_FALLBACK = ['terme_m', 'terme_f', 'langue_etrangere', 'a_interroger', 'terme_search'];

// Libellés lisibles pour les attributs recherchables (fallback = nom technique)
const FIELD_LABELS = {
    terme_m: 'Terme',
    terme_f: 'Terme (féminin)',
    langue_etrangere: 'Équivalent étranger',
    a_interroger: 'À interroger',
    terme_search: 'Terme (recherche)',
    voir: 'Voir aussi',
    voir_text: 'Voir aussi',
    definition_text: 'Définition',
    note_text: 'Note',
    commentaire_text: 'Commentaire',
    attention_text: 'Attention',
};

// Liste blanche des champs EXPOSÉS dans le sélecteur "Rechercher dans" (l'utilisateur
// peut choisir de chercher spécifiquement dans un champ de contenu). Inclut les
// versions *_text (HTML nettoyé). Chaque champ doit être dans les searchableAttributes.
const SEARCH_FIELDS = [
    'terme_m', 'terme_f', 'langue_etrangere', 'a_interroger',
    'definition_text', 'note_text', 'commentaire_text', 'attention_text', 'voir_text',
];

// Champs interrogés par DÉFAUT ("Tous les champs") : uniquement les champs "terme".
// Les champs de contenu (*_text) sont volontairement EXCLUS du défaut pour éviter
// le bruit ; ils ne sont interrogés que si explicitement sélectionnés.
// "terme_search" (suffixes) est ajouté au périmètre par défaut hors mode exact.
const DEFAULT_SEARCH_FIELDS = ['terme_m', 'terme_f', 'langue_etrangere', 'a_interroger'];

// "unordered(terme_m)" / "attr1,attr2" -> ['terme_m'] / ['attr1','attr2']
function parseSearchableAttributes(list) {
    const names = [];
    (list || []).forEach((entry) => {
        const cleaned = String(entry).replace(/^(unordered|ordered)\(/, '').replace(/\)$/, '');
        cleaned.split(',').forEach((a) => {
            const name = a.trim();
            if (name && !names.includes(name)) names.push(name);
        });
    });
    return names;
}

function CustomSearchBox({ exact, setExact }) {
    const { query, refine } = useSearchBox();
    return (
        <div className="srch-top">
            <div className="srch-bar">
                <input
                    className="srch-input"
                    value={query}
                    onChange={e => refine(e.currentTarget.value)}
                    placeholder="Rechercher…"
                    type="text"
                />
                {query && (
                    <button className="srch-clear" onClick={() => refine('')}>✕</button>
                )}
                <button className="srch-btn">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
                         stroke="currentColor" strokeWidth="2.2">
                        <circle cx="8.5" cy="8.5" r="5.5"/>
                        <line x1="13" y1="13" x2="18" y2="18"/>
                    </svg>
                    Rechercher
                </button>
            </div>
            <label className="srch-exact">
                <input
                    type="checkbox"
                    checked={exact}
                    onChange={e => setExact(e.target.checked)}
                />
                Recherche exacte
            </label>
        </div>
    );
}

// Pilote la requête Algolia dans un seul <Configure> (évite les conflits entre
// plusieurs widgets Configure qui définiraient la même clé).
//
// Périmètre de recherche (restrictSearchableAttributes) :
//   - un ou plusieurs champs cochés -> ces champs uniquement (y compris les *_text)
//   - aucun champ coché -> DEFAULT_SEARCH_FIELDS (champs "terme" seulement, PAS les
//     champs de contenu, pour éviter le bruit) + "terme_search" HORS mode exact.
// On intersecte toujours avec les attributs RÉELLEMENT searchable côté Algolia
// (source de vérité) pour ne jamais restreindre à un champ non searchable
// (sinon Algolia rejette la requête).
//
// "Recherche exacte" :
//   - typoTolerance:false    → pas de tolérance aux fautes de frappe
//   - queryType:'prefixNone'  → mot complet exigé ("chat" ne matche pas "chateau")
//   - "terme_search" (suffixes) est écarté (il autoriserait le sous-mot)
function SearchConfigure({ exact, selectedFields, negativeFilters, searchFields }) {
    const loaded = searchFields && searchFields.length ? searchFields : null;

    let restrict;
    if (selectedFields && selectedFields.length) {
        restrict = loaded ? selectedFields.filter((f) => loaded.includes(f)) : selectedFields;
    } else if (loaded) {
        const base = exact ? DEFAULT_SEARCH_FIELDS : [...DEFAULT_SEARCH_FIELDS, 'terme_search'];
        restrict = base.filter((f) => loaded.includes(f));
    }

    return (
        <Configure
            filters={negativeFilters || undefined}
            restrictSearchableAttributes={restrict && restrict.length ? restrict : undefined}
            typoTolerance={exact ? false : undefined}
            queryType={exact ? 'prefixNone' : undefined}
        />
    );
}

function ResultsBar() {
    const { nbHits } = useStats();
    return (
        <div className="srch-results-row">
            <span className="srch-count">{nbHits} résultat{nbHits !== 1 ? 's' : ''}</span>
            <button className="srch-export">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
                     stroke="currentColor" strokeWidth="2">
                    <path d="M10 3v10M6 9l4 4 4-4"/>
                    <rect x="3" y="15" width="14" height="2" rx="1"/>
                </svg>
                Exporter les résultats (.csv ou .xml)
            </button>
        </div>
    );
}

function FilterBadge({ id, label, children, openFilter, setOpenFilter }) {
    const isOpen = openFilter === id;
    return (
        <div className="srch-filter-wrap">
            <button
                className={`srch-badge ${isOpen ? 'srch-badge--active' : ''}`}
                onClick={() => setOpenFilter(isOpen ? null : id)}
            >
                {label}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                     stroke="currentColor" strokeWidth="2">
                    <polyline points={isOpen ? '2,8 6,4 10,8' : '2,4 6,8 10,4'}/>
                </svg>
            </button>
            {/* ✅ Toujours monté, juste caché via CSS */}
            <div className={`srch-filter-panel ${isOpen ? 'srch-filter-panel--open' : 'srch-filter-panel--closed'}`}>
                {children}
            </div>
        </div>
    );
}

function Search() {
    const [openFilter, setOpenFilter] = useState(null);
    const [exclusions, setExclusions] = useState({});
    const [itemsCache, setItemsCache] = useState({});
    const [exact, setExact] = useState(false);

    // Champs recherchables (searchableAttributes) chargés depuis Algolia + champ sélectionné
    const [searchFields, setSearchFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);

    const toggleField = useCallback((field) => {
        setSelectedFields((prev) =>
            prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
        );
    }, []);

    useEffect(() => {
        let active = true;
        const fallback = SEARCHABLE_FALLBACK;
        fetch(`https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/${ALGOLIA_INDEX_FICHES}/settings`, {
            headers: {
                'X-Algolia-API-Key': ALGOLIA_API_KEY,
                'X-Algolia-Application-Id': ALGOLIA_APP_ID,
            },
        })
            .then((r) => r.json())
            .then((s) => {
                if (active) {
                    const fields = parseSearchableAttributes(s.searchableAttributes);
                    setSearchFields(fields.length ? fields : fallback);
                }
            })
            .catch(() => { if (active) setSearchFields(fallback); });
        return () => { active = false; };
    }, []);

    const updateExclusions = useCallback((attribute, values) => {
        setExclusions((prev) => {
            const next = { ...prev };
            if (values.length === 0) {
                delete next[attribute];
            } else {
                next[attribute] = values;
            }
            return next;
        });
    }, []);

    const updateItemsCache = useCallback((attribute, items) => {
        setItemsCache(prev => {
            if (JSON.stringify(prev[attribute]) === JSON.stringify(items)) return prev;
            return { ...prev, [attribute]: items };
        });
    }, []);

    // ✅ Syntaxe filters string — compatible avec facetFilters de RefinementList
    const negativeFilters = Object.entries(exclusions)
        .flatMap(([attr, values]) => values.map(v => `NOT ${attr}:"${v}"`))
        .join(' AND ');

    const badgeProps = { openFilter, setOpenFilter };

    // Options du sélecteur "Rechercher dans" : liste blanche, restreinte aux
    // champs réellement searchable côté Algolia (une fois les settings chargés).
    const availableFields = searchFields.length
        ? SEARCH_FIELDS.filter((f) => searchFields.includes(f))
        : SEARCH_FIELDS;

    return (
        <InstantSearch indexName={ALGOLIA_INDEX_FICHES} searchClient={algoliaSearchClient} routing={true}>
            <SearchConfigure
                exact={exact}
                selectedFields={selectedFields}
                negativeFilters={negativeFilters}
                searchFields={searchFields}
            />

            <div className="srch-root">
                <CustomSearchBox exact={exact} setExact={setExact} />

                <ResultsBar />

                <div className="srch-filters-row">
                    <span className="srch-filter-label">Filtrer par :</span>

                    <FilterBadge id="statut" label="Statut" {...badgeProps}>
                        <CustomRefinementList
                            attribute="statut"
                            exclusions={exclusions.statut || []}
                            onExclude={(v) => updateExclusions('statut', v)}
                            cachedItems={itemsCache.statut || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="num" label="Numéro de fiche & de liste" {...badgeProps}>
                        <SearchByNum attribute="num" />
                    </FilterBadge>

                    <FilterBadge id="commission" label="Collèges" {...badgeProps}>
                        <CustomRefinementList
                            attribute="commission"
                            exclusions={exclusions.commission || []}
                            onExclude={(v) => updateExclusions('commission', v)}
                            cachedItems={itemsCache.commission || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="domaine" label="Domaine" {...badgeProps}>
                        <CustomRefinementList
                            attribute="domaine"
                            exclusions={exclusions.domaine || []}
                            onExclude={(v) => updateExclusions('domaine', v)}
                            cachedItems={itemsCache.domaine || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="sous_domaine" label="Sous-domaine" {...badgeProps}>
                        <CustomRefinementList
                            attribute="sous_domaine"
                            exclusions={exclusions.sous_domaine || []}
                            onExclude={(v) => updateExclusions('sous_domaine', v)}
                            cachedItems={itemsCache.sous_domaine || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="code_langue" label="Code langue" {...badgeProps}>
                        <CustomRefinementList
                            attribute="code_langue"
                            exclusions={exclusions.code_langue || []}
                            onExclude={(v) => updateExclusions('code_langue', v)}
                            cachedItems={itemsCache.code_langue || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="lga_libelle" label="Niveau de langue" {...badgeProps}>
                        <CustomRefinementList
                            attribute="lga_libelle"
                            exclusions={exclusions.lga_libelle || []}
                            onExclude={(v) => updateExclusions('lga_libelle', v)}
                            cachedItems={itemsCache.lga_libelle || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="pays" label="Origine géographique" {...badgeProps}>
                        <CustomRefinementList
                            attribute="pays"
                            exclusions={exclusions.pays || []}
                            onExclude={(v) => updateExclusions('pays', v)}
                            cachedItems={itemsCache.pays || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="source" label="Source" {...badgeProps}>
                        <CustomRefinementList
                            attribute="source"
                            exclusions={exclusions.source || []}
                            onExclude={(v) => updateExclusions('source', v)}
                            cachedItems={itemsCache.source || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="gramm_cat" label="Catégorie grammaticale" {...badgeProps}>
                        <CustomRefinementList
                            attribute="gramm_cat"
                            exclusions={exclusions.gramm_cat || []}
                            onExclude={(v) => updateExclusions('gramm_cat', v)}
                            cachedItems={itemsCache.gramm_cat || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="var_type" label="Type de variante" {...badgeProps}>
                        <CustomRefinementList
                            attribute="variantes.varTypeLib"
                            exclusions={exclusions['variantes.varTypeLib'] || []}
                            onExclude={(v) => updateExclusions('variantes.varTypeLib', v)}
                            cachedItems={itemsCache['variantes.varTypeLib'] || []}
                            onCacheUpdate={updateItemsCache}
                        />
                    </FilterBadge>

                    <FilterBadge id="champs" label="Champs recherchés" {...badgeProps}>
                        <div className="crl-panel">
                            <ul className="crl-list">
                                {availableFields.map((f) => (
                                    <li key={f} className="crl-item">
                                        <label className="crl-label">
                                            <input
                                                type="checkbox"
                                                className="crl-checkbox crl-checkbox--inclure"
                                                checked={selectedFields.includes(f)}
                                                onChange={() => toggleField(f)}
                                            />
                                            <span className="crl-text">{FIELD_LABELS[f] || f}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FilterBadge>
                    <FilterBadge id="date" label="Date" {...badgeProps}>
                        <CustomDateFilter
                            attribute='custom_date'
                            label='Date'
                        />
                    </FilterBadge>

                    <CustomClearRefinements />
                </div>

                <CustomHits editable />
                <Pagination
                    classNames={{
                        list: 'pagination',
                        item: 'page-item',
                        link: 'page-link',
                        selectedItem: 'active',
                        disabledItem: 'disabled',
                    }}
                />
            </div>
        </InstantSearch>
    );
}

export default Search;